/**
 * Key Import Dialog - Dialog for importing keys via QR code
 *
 * Shows a modal with QR scanner for logging in.
 */

import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { QrScanner } from './QrScanner';
import { StoredKeyPair, useCryptoKeyStore } from '@antragsmanager/crypto-lib';
import { verifyKey } from '../api/keysApi';

interface KeyImportDialogProps {
  /** Whether the dialog is open */
  open: boolean;

  /** Callback to close the dialog */
  onClose: () => void;

  /** Callback when key is successfully imported */
  onKeyImported: (keyPair: StoredKeyPair) => void;

  /** Title for the dialog */
  title?: string;
}

export function KeyImportDialog({
  open,
  onClose,
  onKeyImported,
  title = 'Anmelden',
}: KeyImportDialogProps) {
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const { importAndActivateKey } = useCryptoKeyStore();

  // Handle successful QR scan
  const handleKeyScanned = useCallback(
    async (keyPair: StoredKeyPair) => {
      setError(null);
      setIsVerifying(true);

      try {
        // Verify the key with the backend
        const verifyResult = await verifyKey(keyPair.keyId);

        if (!verifyResult.valid) {
          setError('Dieser Schlüssel ist nicht mehr gültig oder wurde widerrufen.');
          setIsVerifying(false);
          return;
        }

        // Update key with backend metadata
        const updatedKeyPair: StoredKeyPair = {
          ...keyPair,
          ownerType: verifyResult.ownerType,
          ownerName: verifyResult.ownerName,
        };

        // Import and activate the key
        await importAndActivateKey(updatedKeyPair);

        // Close dialog and notify parent
        onKeyImported(updatedKeyPair);
        onClose();
      } catch (err) {
        // If verification fails (e.g., new key not yet registered), still allow login
        // but without role information
        console.warn('Key verification failed, proceeding with limited access:', err);

        try {
          await importAndActivateKey(keyPair);
          onKeyImported(keyPair);
          onClose();
        } catch (importError) {
          setError(
            importError instanceof Error
              ? importError.message
              : 'Fehler beim Importieren des Schlüssels.'
          );
        }
      } finally {
        setIsVerifying(false);
      }
    },
    [importAndActivateKey, onKeyImported, onClose]
  );

  // Handle scan error
  const handleScanError = useCallback((errorMessage: string) => {
    setError(errorMessage);
  }, []);

  // Handle close
  const handleClose = () => {
    setError(null);
    setIsVerifying(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <QrCodeScannerIcon />
          {title}
        </Box>
      </DialogTitle>

      <DialogContent>
        {isVerifying ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="body1">
              Schlüssel wird überprüft...
            </Typography>
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Scannen Sie den QR-Code von Ihrem Schlüssel-Dokument, um sich anzumelden.
            </Typography>

            <QrScanner
              onKeyScanned={handleKeyScanned}
              onError={handleScanError}
              height={350}
              showFileUpload={true}
            />
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isVerifying}>
          Abbrechen
        </Button>
      </DialogActions>
    </Dialog>
  );
}
